import {Component, DestroyRef, inject, Input, OnInit, signal} from '@angular/core';
import {Permissions} from "../../../../user/permissions";
import {Product} from "../../product";
import {Website} from "../../website";
import {ActivatedRoute} from '@angular/router';
import {FilterProductsByStatePipe} from "../../filter-products-by-state.pipe";
import {FormControl} from '@angular/forms';
import {ActivityAction, ActivityStatus, IActivity} from '../../../../activities/activity';
import * as fromStore from '../../../../store';
import {DbStoreState} from '../../../../store/reducers';
import {Store} from '@ngrx/store';
import {Observable, Subject} from "rxjs";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {DbModalService} from "../../../../modules/modal/modal";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {UpdatePluginsDialogComponent} from "./update-plugins-dialog/update-plugins-dialog.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {OneMonthFreeComponent} from "../../../../shared/components/dialogs/one-month-free/one-month-free.component";

@Component({
    selector: 'db-overview-plugins-widget',
    templateUrl: './plugins-widget.component.html',
    styleUrls: ['./plugins-widget.component.scss'],
    standalone: false
})
export class PluginsWidgetComponent implements OnInit {
  @Input('website') set newWebsite(website: Website) {

    if (website?.id !== this.website?.id && website && this.website) {
      this.removeSubs.next();
      this.website = website;
      this.websiteId = website.id;
      this.addEventHandlers();
    }
  }

  public ActivityAction: any = ActivityAction;
  public website: Website;
  public websiteId: number;
  public permissions: Permissions;
  public isManagable: boolean;
  public loading: boolean;
  public updateAvailableThemes: Product[];
  public updateAvailablePlugins: Product[];
  public selectAll: FormControl;
  public filterBy: FormControl;
  removeSubs = new Subject<void>();

  public _plugins: Product[];

  public _themes: Product[];

  public filteredProducts: Product[];
  public ongoingActivity$: Observable<IActivity[]>;
  public productListUpdateLoading: boolean;
  loadingItems = []

  @Input() demoUser: boolean;
  pluginUpdateStatuses = signal<{ [key: string]: string }>({});
  themeUpdateStatuses = signal<{ [key: string]: string }>({});
  private _destroyRef = inject(DestroyRef);


  constructor(
    private _store: Store<DbStoreState>,
    private _route: ActivatedRoute,
    private _modalService: DbModalService,
    private _filterProductsByStatePipe: FilterProductsByStatePipe,
  ) {
  }

  ngOnInit() {
    this.website = this._route.snapshot.parent.data['website'];
    this.websiteId = this.website.multisiteType === 'subfolder' ? this.website.children[0] : this.website.id;
    this.permissions = this._route.snapshot.parent.data['user'].permissions;
    this.selectAll = new FormControl('');
    this.filterBy = new FormControl('plugins');
    this._listenActivities();
    this.addEventHandlers();
    this.ongoingActivity$ = this._store.select(fromStore.getOngoingActivities);
    this.ongoingActivity$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data): void => {
        if (data) {
          const ongoingPluginsActivities = data.filter((activity): boolean => activity && this.websiteId && activity.websiteId === this.websiteId && activity.meta && activity.meta.product_data && (activity.meta.product_data.type === "plugin" || activity.meta.product_data.type === "theme") && !(activity.status === ActivityStatus.DONE || activity.status === ActivityStatus.FAILED));
          this.productListUpdateLoading = !!(ongoingPluginsActivities && ongoingPluginsActivities.length)
        }
      });
  }

  private _listenActivities() {
    this._store.select(fromStore.getActivities)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(activities => {
        const ongoingPluginsActivities = activities.filter((activity): boolean => activity && this.websiteId && activity.websiteId === this.websiteId && activity.meta && activity.meta.product_data && (activity.meta.product_data.type === "plugin" || activity.meta.product_data.type === "theme"));
        ongoingPluginsActivities.map((activity: IActivity) => {
          if (activity.status === ActivityStatus.DONE) {
            this._removeFromLoading(activity.meta.product_data.slug)
            if (activity.meta.product_data.type === "plugin") {
              this.pluginUpdateStatuses.update(statuses => ({
                ...statuses,
                [activity.meta.product_data.slug]: 'success'
              }));
            } else {
              this.themeUpdateStatuses.update(statuses => ({
                ...statuses,
                [activity.meta.product_data.slug]: 'success'
              }));
            }
          } else if (activity.status === ActivityStatus.FAILED) {
            this._removeFromLoading(activity.meta.product_data.slug)
            if (activity.meta.product_data.type === "plugin") {
              this.pluginUpdateStatuses.update(statuses => ({
                ...statuses,
                [activity.meta.product_data.slug]: 'failed'
              }));
            } else {
              this.themeUpdateStatuses.update(statuses => ({
                ...statuses,
                [activity.meta.product_data.slug]: 'failed'
              }));
            }
          }
        })
      })
  }

  private _removeFromLoading(slug: string) {
    this.loadingItems = this.loadingItems.filter(
      item => item !== slug
    );
  }


  public toggleAllItemsSelected(selected: boolean): void {
    this.filteredProducts.forEach((product: Product): void => {
      product.selected = selected;
    });
  }

  public onUpdatesStateChange($event): void {
    this.filterBy.setValue($event.tabId);
    this.selectAll.setValue("");
    this.toggleAllItemsSelected(false);
  }

  private filteredProductsData(): void {
    let filteredProducts: Product[];
    if (this.filterBy.value === "plugins") {
      filteredProducts = this.updateAvailablePlugins;
    } else {
      filteredProducts = this.updateAvailableThemes;
    }
    this.filteredProducts = filteredProducts;
    if (this.selectAll.value) {
      this.toggleAllItemsSelected(true);
    }
  }

  public updateAll() {
    this.showConfirmDeactivateAndDeleteDialog();
  }

  public openCheckoutDialog(): void {
    if (this.demoUser) {
      const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
      const options = {
        overflowHidden: false,
        backdropClass: 'blurred',
      };
      if (!firstTime) {
        options['width'] = 1240;
      } else {
        options['width'] = 460;
        options['height'] = 600;
      }
      const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
      modal.componentInstance.showFirstTime = firstTime;
    } else {
      const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
      modal.componentInstance.mode = 'feature';    }
  }

  public applyBulkAction(bulkaction, deactivate, items = null, updateAll = false): void {
    const bulkActionItems: Product[] = items ? items : this.getBulkActionItems(ActivityAction.UPDATE);
    if (!updateAll) {
      this.loadingItems = bulkActionItems.map((bulkActionItem: Product) => bulkActionItem.slug);
    }
    if (bulkaction) {
      if (bulkActionItems.length) {
        this._store.dispatch(new fromStore.InitWebsiteProductAction({
          website: this.website,
          products: bulkActionItems,
          action: bulkaction,
          network: 1
        }));
        this.updateSelectAllState();
      }
    }
  }

  public onItemToggled(): void {
    this.updateSelectAllState();
  }

  private updateSelectAllState(): void {
    this.selectAll.setValue(this.filteredProducts.length && !this.filteredProducts.find((product: Product): boolean => !product.selected));
  }


  private getBulkActionItems(bulkaction): Product[] {
    const bulkAction = bulkaction;
    return this.filteredProducts.reduce((bulkActionItems: any[], plugin: Product): number[] => {
      const canApplyAction = true;
      if (plugin.selected && (canApplyAction || plugin.canApplyAction(bulkAction))) {
        bulkActionItems.push(plugin);
      }
      return bulkActionItems;
    }, []);
  }

  public get selectedPluginsAndAddons(): Product[] {
    if (!this.filteredProducts) {
      return [];
    }

    return this.filteredProducts.reduce((selectedProducts: Product[], pluginWithAddons: any): Product[] => {
      const plugin: Product = pluginWithAddons;

      if (plugin.selected) {
        selectedProducts.push(plugin);
      }

      return selectedProducts;
    }, []);
  }

  private addEventHandlers(): void {
    this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean) => !!loaded),
        takeUntilDestroyed(this._destroyRef),
        takeUntil(this.removeSubs),
        switchMap(() => this._store.select(fromStore.getWebsite(this.websiteId))),
        first()
      ).subscribe((website: Website): void => {
      this.website = website;
    })

    this._store.select(fromStore.getWebsitePluginsAndExtensions(this.websiteId))
      .pipe(takeUntilDestroyed(this._destroyRef), takeUntil(this.removeSubs),)
      .subscribe((plugins: Product[]) => {
        this._plugins = plugins;
        if (Object.keys(this.pluginUpdateStatuses()).length) {
          setTimeout(() => {
            this.updateAvailablePlugins = this._filterProductsByStatePipe.transform(this._plugins, "updateAvailable");
            this.filteredProductsData();
          }, 3000)
        } else {
          this.updateAvailablePlugins = this._filterProductsByStatePipe.transform(this._plugins, "updateAvailable");
          this.filteredProductsData();
        }

      })

    this._store.select(fromStore.getWebsiteThemes(this.websiteId))
      .pipe(takeUntilDestroyed(this._destroyRef), takeUntil(this.removeSubs))
      .subscribe((themes: Product[]) => {
        this._themes = themes;
        if (Object.keys(this.themeUpdateStatuses()).length) {
          setTimeout(() => {
            this.updateAvailableThemes = this._filterProductsByStatePipe.transform(this._themes, "updateAvailable");
            this.filteredProductsData();
          }, 3000)
        } else {
          this.updateAvailableThemes = this._filterProductsByStatePipe.transform(this._themes, "updateAvailable");
          this.filteredProductsData();
        }
      })

    this.filterBy.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef), takeUntil(this.removeSubs))
      .subscribe((): void => {
        this.filteredProductsData();
      })

  }

  private showConfirmDeactivateAndDeleteDialog(): void {
    const modal = this._modalService.open(UpdatePluginsDialogComponent, {hasCloseBtn: false});
    modal.componentInstance.pluginsCount = this.updateAvailablePlugins.length;
    modal.componentInstance.themesCount = this.updateAvailableThemes.length;
    modal.closed.pipe(first()).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.loadingItems = [...this.updateAvailablePlugins, ...this.updateAvailableThemes].map((bulkActionItem: Product) => bulkActionItem.slug);
        this.applyBulkAction(ActivityAction.UPDATE, false, this.updateAvailablePlugins, true);
        this.applyBulkAction(ActivityAction.UPDATE, false, this.updateAvailableThemes, true);
      }
    });
  }

  updateSingleProduct(product: Product) {
    this.loadingItems = [product.slug]
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [product],
      action: ActivityAction.UPDATE,
      network: 1
    }));
  }

}
